import React, { useState, useEffect } from 'react';
import { cleanupData } from '../../services/SettingsService';
import { clearAllLogs } from '../../services/LogsService';
import { resetCache } from '../../services/BeholderService';
import { cleanTestEnvironment } from '../../services/StrategiesService';
import { restartBackend } from '../../services/HydraService';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getCurrentMarket } from '../../utils/MarketStorage';
import { confirmDialog } from '../../utils/ConfirmDialog';

const ITEMS = [
    { key: 'orders',           label: 'Ordens',                   hasEnv: true,  hasMarket: true  },
    { key: 'automations',      label: 'Automações',               hasEnv: true,  hasMarket: true  },
    { key: 'aiSignals',        label: 'Sinais de IA (histórico)', hasEnv: false, hasMarket: false },
    { key: 'strategies',       label: 'Estratégias',              hasEnv: false, hasMarket: true  },
    { key: 'orderTemplates',   label: 'Templates de Ordem',       hasEnv: false, hasMarket: true  },
    { key: 'monitors',         label: 'Monitores',                hasEnv: false, hasMarket: false },
    { key: 'withdrawTemplates',label: 'Templates de Saque',       hasEnv: false, hasMarket: false },
    { key: 'webhooks',         label: 'Webhooks',                 hasEnv: false, hasMarket: false },
];

const ENVS = [['test', 'Teste'], ['production', 'Produção'], ['all', 'Ambos']];
const ENV_LABEL = { test: 'Teste', production: 'Produção', all: 'Ambos' };
const MARKETS = [['', 'Ambos'], ['spot', '🪙 Spot'], ['futures', '⚡ Futuros']];

// Hook: ambiente "atual" do app (lente global). Reage à troca no topo da UI.
function useCurrentEnvironment() {
    const [env, setEnv] = useState(getCurrentEnvironment);
    useEffect(() => subscribeToCurrentEnvironment(setEnv), []);
    return env;
}

function RestartTestsPanel() {
    const [loading, setLoading] = useState(false);       // soft: mantém estratégias
    const [loadingHard, setLoadingHard] = useState(false); // hard: apaga estratégias
    const [result, setResult] = useState(null);

    async function onRestart() {
        const ok = await confirmDialog({
            title: 'Reiniciar testes (mantém estratégias)',
            message: 'Recomeço limpo do ambiente de TESTE:\n\n' +
                '• Apaga ordens de teste\n' +
                '• Apaga o histórico de sinais de IA\n' +
                '• Limpa todos os logs\n' +
                '• Reseta o cache Redis e repopula os monitores\n\n' +
                '✅ MANTÉM estratégias, automações, templates e monitores.\n' +
                'Não afeta dados de produção.\n\nEsta ação não pode ser desfeita.',
            confirmLabel: 'Reiniciar testes',
            confirmVariant: 'warning'
        });
        if (!ok) return;
        setLoading(true);
        setResult(null);
        try {
            const parts = [];
            const clean = await cleanupData(['orders', 'aiSignals'], 'test', '');
            const d = clean.deleted || {};
            parts.push(`${d.orders || 0} ordem(ns), ${d.aiSignals || 0} sinal(is) de IA`);
            const logs = await clearAllLogs();
            parts.push(`${logs.cleared || 0} log(s)`);
            const cache = await resetCache();
            parts.push(`cache limpo, ${cache.restarted || 0} monitor(es) repopulando`);
            setResult({ ok: true, message: `Testes reiniciados: ${parts.join(' · ')}.` });
        } catch (err) {
            setResult({ ok: false, message: err.response?.data?.error || err.response?.data || err.message || 'Erro.' });
        } finally {
            setLoading(false);
        }
    }

    async function onHardClean() {
        const ok = await confirmDialog({
            title: 'Zerar testes (apaga estratégias)',
            message: 'Apaga TODO o ambiente de TESTE para reavaliação do zero:\n\n' +
                '• Estratégias de teste (com automações, grades e templates de ordem)\n' +
                '• Automações de teste avulsas\n' +
                '• Ordens de teste\n' +
                '• Monitores que ficarem órfãos\n' +
                '• Posições em memória dos símbolos afetados\n\n' +
                '❌ Suas estratégias de teste DEIXAM DE EXISTIR.\n' +
                'Depois, recrie pelos templates na tela Estratégias.\n' +
                'Não afeta dados de produção.\n\nEsta ação não pode ser desfeita.',
            confirmLabel: 'Zerar testes',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        setLoadingHard(true);
        setResult(null);
        try {
            const r = await cleanTestEnvironment();
            const logs = await clearAllLogs();
            setResult({
                ok: true,
                message: `Ambiente de teste zerado: ${r.strategies || 0} estratégia(s), ${r.automations || 0} automação(ões), ` +
                    `${r.orders || 0} ordem(ns), ${r.monitors || 0} monitor(es), ${logs.cleared || 0} log(s). ` +
                    `Recrie suas estratégias em Estratégias → ✨ Usar template.`
            });
        } catch (err) {
            setResult({ ok: false, message: err.response?.data?.error || err.response?.data || err.message || 'Erro.' });
        } finally {
            setLoadingHard(false);
        }
    }

    return (
        <div className='card border-0 shadow p-3 mb-4'>
            <h5 className='fw-bold mb-3'>🔁 Recomeço de Testes</h5>
            <p className='text-muted small mb-2'>
                Dois níveis de recomeço do ambiente de <strong>teste</strong> — produção nunca é tocada:
            </p>
            <ul className='text-muted small mb-3' style={{ paddingLeft: 18 }}>
                <li><strong>Reiniciar</strong> — zera os resultados (ordens, sinais de IA, logs, cache). <strong>Mantém</strong> suas estratégias rodando.</li>
                <li><strong>Zerar</strong> — apaga <strong>também</strong> as estratégias, automações e monitores de teste. Reavaliação do zero: depois recrie pelos templates.</li>
            </ul>
            {result && (
                <div className={`alert py-2 mb-3 ${result.ok ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 13 }}>
                    {result.message}
                </div>
            )}
            <div className='d-flex gap-2 flex-wrap'>
                <button className='btn btn-sm btn-warning' disabled={loading || loadingHard} onClick={onRestart}>
                    {loading ? 'Reiniciando...' : '🔁 Reiniciar Testes (mantém estratégias)'}
                </button>
                <button className='btn btn-sm btn-danger' disabled={loading || loadingHard} onClick={onHardClean}>
                    {loadingHard ? 'Zerando...' : '🧹 Zerar Testes (apaga estratégias)'}
                </button>
            </div>
        </div>
    );
}

function LogCleanupPanel() {
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [logResult, setLogResult] = useState(null);

    async function onClearLogs() {
        const ok = await confirmDialog({
            title: 'Limpar todos os logs',
            message: 'Limpar todos os arquivos de log?\n\nEsta ação não pode ser desfeita.',
            confirmLabel: 'Limpar',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        setLoadingLogs(true);
        setLogResult(null);
        try {
            const r = await clearAllLogs();
            setLogResult({ ok: true, message: `${r.cleared} arquivo(s) de log limpo(s).` });
        } catch (err) {
            setLogResult({ ok: false, message: err.response?.data?.error || err.message || 'Erro.' });
        } finally {
            setLoadingLogs(false);
        }
    }

    return (
        <div className='card border-0 shadow p-3 mb-4'>
            <h5 className='fw-bold mb-3'>📋 Limpeza de Logs</h5>
            <p className='text-muted small mb-3'>Esvazia todos os arquivos de log de automações e monitores. Não remove os arquivos — apenas o conteúdo.</p>
            {logResult && (
                <div className={`alert py-2 mb-3 ${logResult.ok ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 13 }}>
                    {logResult.message}
                </div>
            )}
            <div>
                <button className='btn btn-sm btn-outline-danger' disabled={loadingLogs} onClick={onClearLogs}>
                    {loadingLogs ? 'Limpando...' : '🗑️ Limpar Todos os Logs'}
                </button>
            </div>
        </div>
    );
}

function CleanupPanel() {
    const currentEnv = useCurrentEnvironment();
    const [selected, setSelected] = useState({ orders: true, automations: false, aiSignals: false, strategies: false,
        orderTemplates: false, monitors: false, withdrawTemplates: false, webhooks: false });
    // Default segue o ambiente atual do app (a "lente" em que o usuário está operando).
    const [environment, setEnvironment] = useState(currentEnv);
    // Mercado a limpar: '' = ambos. Default segue o mercado atual do app (contexto).
    const [market, setMarket] = useState(getCurrentMarket() || '');
    const [confirmText, setConfirmText] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const touchesProduction = environment === 'production' || environment === 'all';
    const selectedKeys = Object.keys(selected).filter(k => selected[k]);
    const noEnvSelected = selectedKeys.filter(k => ITEMS.find(i => i.key === k && !i.hasEnv));
    // Itens sem mercado serão PULADOS quando um mercado específico está selecionado.
    const skippedByMarket = market ? selectedKeys.filter(k => ITEMS.find(i => i.key === k && !i.hasMarket)) : [];
    const canExecute = selectedKeys.length > 0 && (!touchesProduction || confirmText === 'confirmar');

    function toggle(key) {
        setSelected(prev => ({ ...prev, [key]: !prev[key] }));
        setResult(null);
    }

    async function onExecute() {
        setLoading(true);
        setResult(null);
        try {
            const r = await cleanupData(selectedKeys, environment, confirmText, market || undefined);
            const summary = Object.entries(r.deleted)
                .map(([k, n]) => `${n} ${ITEMS.find(i => i.key === k)?.label || k}`)
                .join(', ');
            const skippedMsg = r.skipped && r.skipped.length
                ? ` (pulados por mercado: ${r.skipped.map(k => ITEMS.find(i => i.key === k)?.label || k).join(', ')})`
                : '';
            setResult({ ok: true, message: `Limpeza concluída: ${summary || 'nenhum registro encontrado'}${skippedMsg}.` });
            setConfirmText('');
        } catch (err) {
            setResult({ ok: false, message: err.response?.data?.error || err.message || 'Erro.' });
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className='card border-0 shadow p-3 mb-4'>
            <h5 className='fw-bold mb-3'>🗑️ Limpeza de Dados</h5>

            <div className='alert alert-light border py-2 mb-3' style={{ fontSize: 13 }}>
                Ambiente atual do app:{' '}
                <span className={`badge ${currentEnv === 'production' ? 'bg-danger' : 'bg-secondary'}`}>
                    {ENV_LABEL[currentEnv]}
                </span>{' '}
                — a limpeza já vem selecionada nesse ambiente. Confira antes de executar.
            </div>

            <div className='mb-3'>
                <label className='form-label fw-semibold'>Ambiente a limpar</label>
                <div className='d-flex gap-3'>
                    {ENVS.map(([val, label]) => (
                        <div key={val} className='form-check'>
                            <input className='form-check-input' type='radio' id={`env-${val}`}
                                checked={environment === val}
                                onChange={() => { setEnvironment(val); setConfirmText(''); setResult(null); }} />
                            <label className='form-check-label' htmlFor={`env-${val}`}>
                                {label}
                                {val === currentEnv && <span className='text-primary ms-1' style={{ fontSize: 11 }}>(atual)</span>}
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            <div className='mb-3'>
                <label className='form-label fw-semibold'>Mercado a limpar</label>
                <div className='d-flex gap-3'>
                    {MARKETS.map(([val, label]) => (
                        <div key={val || 'all'} className='form-check'>
                            <input className='form-check-input' type='radio' id={`mkt-${val || 'all'}`}
                                checked={market === val}
                                onChange={() => { setMarket(val); setResult(null); }} />
                            <label className='form-check-label' htmlFor={`mkt-${val || 'all'}`}>
                                {label}
                                {val && val === getCurrentMarket() && <span className='text-primary ms-1' style={{ fontSize: 11 }}>(atual)</span>}
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            <div className='mb-3'>
                <label className='form-label fw-semibold'>O que limpar</label>
                <div className='d-flex flex-wrap gap-3'>
                    {ITEMS.map(item => (
                        <div key={item.key} className='form-check'>
                            <input className='form-check-input' type='checkbox' id={`chk-${item.key}`}
                                checked={!!selected[item.key]} onChange={() => toggle(item.key)} />
                            <label className='form-check-label' htmlFor={`chk-${item.key}`}>
                                {item.label}
                                {!item.hasEnv && <span className='text-muted ms-1' style={{ fontSize: 11 }}>(todos)</span>}
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            {skippedByMarket.length > 0 && (
                <div className='alert alert-info py-2 mb-3' style={{ fontSize: 13 }}>
                    ℹ️ {skippedByMarket.map(k => ITEMS.find(i => i.key === k)?.label).join(', ')} não têm mercado — serão <strong>pulados</strong> com o filtro de mercado ativo (não se apaga spot/compartilhado numa limpeza de futuros).
                </div>
            )}

            {environment !== 'all' && noEnvSelected.length > 0 && (
                <div className='alert alert-warning py-2 mb-3' style={{ fontSize: 13 }}>
                    ⚠️ {noEnvSelected.map(k => ITEMS.find(i => i.key === k)?.label).join(', ')} não têm filtro por ambiente — serão deletados independente do ambiente selecionado.
                </div>
            )}

            {touchesProduction && (
                <div className='alert alert-danger py-2 mb-3' style={{ fontSize: 13 }}>
                    <strong>🔴 Atenção:</strong> esta limpeza inclui <strong>produção</strong>
                    {environment === 'all' ? ' (ambos os ambientes serão afetados)' : ''} — dados serão{' '}
                    <strong>permanentemente</strong> deletados.<br />
                    <div className='mt-2'>
                        <input type='text' className='form-control form-control-sm' style={{ maxWidth: 220 }}
                            placeholder='Digite "confirmar" para continuar'
                            value={confirmText} onChange={e => setConfirmText(e.target.value)} />
                    </div>
                </div>
            )}

            {result && (
                <div className={`alert py-2 mb-3 ${result.ok ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 13 }}>
                    {result.message}
                </div>
            )}

            <div>
                <button className={`btn btn-sm ${touchesProduction ? 'btn-danger' : 'btn-warning'}`}
                    disabled={!canExecute || loading}
                    onClick={onExecute}>
                    {loading ? 'Limpando...' : `🗑️ Executar Limpeza (${ENV_LABEL[environment]})`}
                </button>
            </div>
        </div>
    );
}

function CacheResetPanel() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    async function onReset() {
        const ok = await confirmDialog({
            title: 'Limpar cache Redis',
            message: 'Isso apaga todos os dados de mercado em memória (candles, preços, regime, sinais de IA) e reinicia automaticamente todos os monitores ativos para repopular.\n\nNão afeta banco de dados nem configurações.',
            confirmLabel: 'Limpar e Repopular',
            confirmVariant: 'warning'
        });
        if (!ok) return;
        setLoading(true);
        setResult(null);
        try {
            const r = await resetCache();
            setResult({ ok: true, message: `Cache limpo. ${r.restarted} monitor(es) reiniciado(s) para repopular.` });
        } catch (err) {
            setResult({ ok: false, message: err.response?.data || err.message || 'Erro.' });
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className='card border-0 shadow p-3 mb-4'>
            <h5 className='fw-bold mb-3'>🔄 Cache Redis</h5>
            <p className='text-muted small mb-3'>
                Limpa todos os dados de mercado em memória (candles, preços, regime de mercado, sinais de IA) e reinicia os monitores ativos para repopular imediatamente.
                Use quando os dados estiverem desatualizados ou o beholder estiver aguardando índices que deveriam existir.
            </p>
            {result && (
                <div className={`alert py-2 mb-3 ${result.ok ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 13 }}>
                    {result.message}
                </div>
            )}
            <div>
                <button className='btn btn-sm btn-outline-warning' disabled={loading} onClick={onReset}>
                    {loading ? 'Limpando...' : '🔄 Limpar Cache e Repopular'}
                </button>
            </div>
        </div>
    );
}

function BackendRestartPanel() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    async function onRestart() {
        const ok = await confirmDialog({
            title: 'Reiniciar o backend',
            message: 'Reinicia o processo do servidor (o pm2 sobe de novo sozinho):\n\n' +
                '• Memória (Redis) é totalmente limpa e repopulada no boot\n' +
                '• Estratégias e automações ativas voltam a rodar do banco\n' +
                '• Estado interno do processo é zerado\n\n' +
                'A conexão cai por ~20-40 segundos. Recomendado logo após o "Zerar Testes".\n\n' +
                'Requer perfil de administrador.',
            confirmLabel: 'Reiniciar backend',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        setLoading(true);
        setResult(null);
        try {
            const r = await restartBackend();
            setResult({ ok: true, message: r.message || 'Reiniciando — a conexão volta em ~20-40s.' });
        } catch (err) {
            setResult({ ok: false, message: err.response?.status === 403
                ? 'Apenas administradores podem reiniciar o backend.'
                : (err.response?.data?.error || err.response?.data || err.message || 'Erro.') });
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className='card border-0 shadow p-3 mb-4'>
            <h5 className='fw-bold mb-3'>⚡ Sistema</h5>
            <p className='text-muted small mb-3'>
                Reinicia o processo do backend na VPS. O boot limpa a memória (Redis), recarrega tudo do banco e zera o
                estado interno — equivale a um recomeço completo do runtime, sem tocar em nenhum dado. A tela perde a
                conexão por ~20-40s (o aviso de reconexão aparece no topo) e volta sozinha.
            </p>
            {result && (
                <div className={`alert py-2 mb-3 ${result.ok ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 13 }}>
                    {result.message}
                </div>
            )}
            <div>
                <button className='btn btn-sm btn-outline-danger' disabled={loading} onClick={onRestart}>
                    {loading ? 'Enviando...' : '⚡ Reiniciar Backend'}
                </button>
            </div>
        </div>
    );
}

function CleanupPanelRoot() {
    return (
        <React.Fragment>
            <RestartTestsPanel />
            <CleanupPanel />
            <LogCleanupPanel />
            <CacheResetPanel />
            <BackendRestartPanel />
        </React.Fragment>
    );
}

export default CleanupPanelRoot;
